import { memo, useDeferredValue, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Button,
  Portal,
  SearchBox,
  Switch,
  Tab,
  TabList,
  Tooltip,
  tokens,
} from "@fluentui/react-components";
import {
  Dismiss20Regular,
  Flag20Regular,
  HandPoint20Regular,
  LockClosed12Regular,
  Play20Regular,
  Target20Regular,
} from "@fluentui/react-icons";
import { helpText } from "./strings";
import {
  HELP_LANGUAGES,
  groupStepsBySection,
  localizedContext,
  localizedPage,
  localizedSection,
  localizedStep,
  stepMatchesQuery,
  type HelpLanguage,
  type HelpStep,
  type HelpViewer,
  type PageHelp,
} from "./types";
import { HELP_PANEL_ATTR, locateElement } from "./dom";
import { revealHelpStep, revealRecipe, useRevealNavigation, type RevealNavigation } from "./reveal";
import { EnPill } from "./HelpTour";
import { HelpAsk } from "./HelpAsk";
import { DRAWER_TRANSITION_MS, DRAWER_WIDTH, PEEK_WIDTH, PERMISSION_LABEL_KEY, Z } from "./ui";

type DrawerTab = "page" | "ask";

interface Props {
  open: boolean;
  page: PageHelp;
  /** Already filtered by permission — a hidden step appears in no list and no count. */
  steps: HelpStep[];
  viewer: HelpViewer;
  language: HelpLanguage;
  onLanguageChange: (language: HelpLanguage) => void;
  floating: boolean;
  onFloatingChange: (floating: boolean) => void;
  notify: (message: string) => void;
  onClose: () => void;
  onStartTour: (from?: HelpStep) => void;
  onInspect: () => void;
}

export function HelpDrawer(props: Props) {
  const { open, page, steps, language, onClose, onStartTour, onInspect } = props;
  const t = helpText(language);
  const navigation = useRevealNavigation(language);
  const [tab, setTab] = useState<DrawerTab>("page");
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [peeking, setPeeking] = useState(false);

  const pageText = localizedPage(page, language);
  const groups = useMemo(
    () =>
      groupStepsBySection(
        steps.filter((s) => stepMatchesQuery(s, deferredQuery)),
        page.sections
      ),
    [steps, deferredQuery, page.sections]
  );
  const matchCount = groups.reduce((n, g) => n + g.steps.length, 0);

  useEffect(() => {
    if (!open) setPeeking(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  const transform = !open
    ? "translateX(100%)"
    : peeking
      ? `translateX(${DRAWER_WIDTH - PEEK_WIDTH}px)`
      : "translateX(0)";

  return (
    <Portal>
      <div {...{ [HELP_PANEL_ATTR]: "drawer" }}>
        {/* scrim — transparent while peeking so the located control is readable */}
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: Z.drawer,
            backgroundColor: open && !peeking ? "rgba(0, 0, 0, 0.25)" : "transparent",
            pointerEvents: open && !peeking ? "auto" : "none",
            transition: `background-color ${DRAWER_TRANSITION_MS}ms`,
          }}
        />
        <aside
          role="dialog"
          aria-modal={false}
          aria-label={t("help.drawer.title", "Page guide")}
          aria-hidden={!open}
          data-help="help-drawer"
          onMouseEnter={() => peeking && setPeeking(false)}
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: DRAWER_WIDTH,
            maxWidth: "100vw",
            zIndex: Z.drawer,
            display: "flex",
            flexDirection: "column",
            background: tokens.colorNeutralBackground1,
            color: tokens.colorNeutralForeground1,
            boxShadow: open ? tokens.shadow64 : "none",
            transform,
            transition: `transform ${DRAWER_TRANSITION_MS}ms ease-out`,
            visibility: open ? "visible" : "hidden",
          }}
        >
          {/* header: eyebrow, page title, tabs */}
          <header style={{ padding: "16px 16px 0 20px", borderBottom: `1px solid ${tokens.colorNeutralStroke2}` }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={EYEBROW}>{t("help.drawer.eyebrow", "Page guide")}</div>
                <div style={{ fontSize: 18, fontWeight: 600, display: "flex", gap: 8, alignItems: "center" }}>
                  {pageText.value.title}
                  {!pageText.translated && <EnPill language={language} />}
                </div>
              </div>
              <div
                data-help="help-language"
                role="group"
                aria-label={t("help.drawer.language")}
                style={{ display: "flex", gap: 2, alignItems: "center", marginTop: 2 }}
              >
                {HELP_LANGUAGES.map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    lang={lang}
                    aria-pressed={language === lang}
                    onClick={() => props.onLanguageChange(lang)}
                    style={{
                      border: 0,
                      borderRadius: 4,
                      padding: "3px 8px",
                      cursor: "pointer",
                      font: "inherit",
                      fontSize: 11,
                      fontWeight: 600,
                      color: language === lang ? tokens.colorNeutralForeground1 : tokens.colorNeutralForeground3,
                      background: language === lang ? tokens.colorNeutralBackground3 : "transparent",
                    }}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
              <Button
                appearance="subtle"
                icon={<Dismiss20Regular />}
                aria-label={t("help.drawer.close", "Close page guide")}
                onClick={onClose}
              />
            </div>
            <TabList
              size="small"
              selectedValue={tab}
              onTabSelect={(_, d) => setTab(d.value as DrawerTab)}
              style={{ marginTop: 6 }}
            >
              <Tab value="page" data-help="help-tab-page">
                {t("help.drawer.tabPage", "This page")}
              </Tab>
              <Tab value="ask" data-help="help-tab-ask">
                {t("help.drawer.tabAsk", "Ask")}
              </Tab>
            </TabList>
          </header>

          {/* "This page" — kept mounted while hidden so scroll position and search survive */}
          <div
            style={{ overflowY: "auto", flex: 1, padding: "14px 20px 24px", display: tab === "page" ? "block" : "none" }}
          >
            <p style={{ margin: "0 0 14px", lineHeight: 1.55, fontSize: 13.5 }}>{pageText.value.intro}</p>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button
                data-help="help-start-tour"
                appearance="primary"
                icon={<Flag20Regular />}
                onClick={() => onStartTour()}
              >
                {t("help.drawer.startTour", "Show me around")}
              </Button>
              <Button
                data-help="help-inspect-button"
                appearance="outline"
                icon={<HandPoint20Regular />}
                onClick={onInspect}
              >
                {t("help.drawer.inspect", "Point at a field")}
              </Button>
            </div>
            <p style={{ margin: "8px 0 14px", fontSize: 12, lineHeight: 1.5, color: tokens.colorNeutralForeground3 }}>
              {t(
                "help.drawer.modesHint",
                'The tour visits what is on screen right now — or only the open dialog, when there is one; its step counter jumps to any of them. "Point at a field" answers the other way round: click the control you are looking at and read what it does.'
              )}
            </p>

            <SearchBox
              data-help="help-page-search"
              style={{ width: "100%", maxWidth: "none" }}
              placeholder={t("help.drawer.searchAll", "Search all {{count}} entries…", { count: steps.length })}
              value={query}
              onChange={(_, d) => setQuery(d.value)}
            />
            {query && (
              <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3, marginTop: 6 }}>
                {t("help.drawer.matches", "{{count}} of {{total}} steps match", {
                  count: matchCount,
                  total: steps.length,
                })}
              </div>
            )}

            {groups.length === 0 && (
              <div style={{ color: tokens.colorNeutralForeground3, marginTop: 16 }}>
                {t("help.search.noResults", "No step matches.")}
              </div>
            )}

            {groups.map((group) => (
              <section key={group.section} style={{ marginTop: 18 }}>
                <SectionHeading>{localizedSection(page, group.section, language).value}</SectionHeading>
                {group.steps.map((step) => (
                  <StepCard
                    key={step.selector}
                    page={page}
                    step={step}
                    language={language}
                    onPeek={setPeeking}
                    onStartTour={onStartTour}
                    navigation={navigation}
                  />
                ))}
              </section>
            ))}

            {!query && pageText.value.tips.length > 0 && (
              <section style={{ marginTop: 18 }}>
                <SectionHeading>{t("help.drawer.tips", "Good to know")}</SectionHeading>
                {pageText.value.tips.map((tip) => (
                  <p key={tip} style={{ margin: "0 0 10px", fontSize: 13, lineHeight: 1.5 }}>
                    {tip}
                  </p>
                ))}
              </section>
            )}
          </div>

          {/* "Ask" — kept mounted too, so a typed question survives a peek or a tab switch */}
          <div
            style={{ overflowY: "auto", flex: 1, padding: "14px 20px 24px", display: tab === "ask" ? "block" : "none" }}
          >
            <HelpAsk
              viewer={props.viewer}
              language={language}
              onHandover={onClose}
              notify={props.notify}
            />
          </div>

          <footer
            style={{
              padding: "10px 20px 12px",
              borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
              background: tokens.colorNeutralBackground2,
            }}
          >
            <Switch
              data-help="help-floating-toggle"
              checked={props.floating}
              onChange={(_, d) => props.onFloatingChange(d.checked)}
              label={
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  {t("help.drawer.floating", "Floating “?” button")}
                </span>
              }
              aria-label={t("help.drawer.floatingAria", "Show a floating page-guide button")}
            />
            <div style={{ fontSize: 12, lineHeight: 1.45, color: tokens.colorNeutralForeground3, marginTop: 2 }}>
              {props.floating
                ? t(
                    "help.drawer.floatingOn",
                    "Shown in the bottom-right corner, where it can cover a table's scroll area and row buttons."
                  )
                : t(
                    "help.drawer.floatingOff",
                    "Off — the “?” in the top bar opens this guide, and stays clickable while a dialog is open."
                  )}
            </div>
          </footer>
        </aside>
      </div>
    </Portal>
  );
}

const EYEBROW = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 1.2,
  textTransform: "uppercase" as const,
  color: tokens.colorNeutralForeground3,
};

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 8px" }}>
      <span style={EYEBROW}>{children}</span>
      <span style={{ flex: 1, height: 1, background: tokens.colorNeutralStroke2 }} />
    </div>
  );
}

interface CardProps {
  page: PageHelp;
  step: HelpStep;
  language: HelpLanguage;
  onPeek: (peeking: boolean) => void;
  onStartTour: (from?: HelpStep) => void;
  navigation: RevealNavigation;
}

const StepCard = memo(function StepCard({ page, step, language, onPeek, onStartTour, navigation }: CardProps) {
  const t = helpText(language);
  // Learned on demand: the drawer never probes the DOM up front, so a card is never greyed on a stale measurement.
  const [missing, setMissing] = useState(false);
  const text = localizedStep(step, language);
  const context = step.context ? localizedContext(page, step.context, language) : null;
  const permission = step.requires ? PERMISSION_LABEL_KEY[step.requires] : null;
  const title = text.value.title;

  return (
    <article
      style={{
        display: "flex",
        gap: 6,
        padding: "12px 10px 12px 14px",
        marginBottom: 8,
        borderRadius: 8,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        background: tokens.colorNeutralBackground1,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 13.5 }}>{title}</span>
          {!text.translated && <EnPill language={language} />}
        </div>
        <p
          style={{
            margin: "4px 0 0",
            fontSize: 13,
            lineHeight: 1.5,
            whiteSpace: "pre-line",
            color: tokens.colorNeutralForeground2,
          }}
        >
          {text.value.body}
        </p>
        {(permission || context) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6, fontSize: 12 }}>
            {permission && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: tokens.colorNeutralForeground3 }}>
                <LockClosed12Regular /> {t(permission[0], permission[1])}
              </span>
            )}
            {context && (
              <span style={{ color: tokens.colorNeutralForeground3 }}>
                {t("help.card.toSeeThis", "To see this:")} {context.value}
              </span>
            )}
          </div>
        )}
        {missing && (
          <div role="status" style={{ marginTop: 6, fontSize: 12, color: tokens.colorPaletteDarkOrangeForeground1 }}>
            {context
              ? t("help.card.notOnScreenContext", "Not on screen right now. {{how}}", { how: context.value })
              : step.optional
                ? t("help.card.notOnScreenOptional", "Not on screen right now — it only appears when there is data for it.")
                : t("help.card.notOnScreen", "Not on screen right now.")}
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Tooltip content={t("help.card.locate", "Show me where")} relationship="description">
          <Button
            size="small"
            appearance="transparent"
            icon={<Target20Regular />}
            aria-label={t("help.card.locateAria", "Locate: {{title}}", { title })}
            onClick={() => {
              if (locateElement(step, { onYield: onPeek })) return setMissing(false);
              if (!revealRecipe(page, step)) return setMissing(true);
              // open the menu / tab / dialog it lives in (or an offer that has it), then point at it
              void revealHelpStep(page, step, navigation).then((shown) =>
                setMissing(!(shown && locateElement(step, { onYield: onPeek })))
              );
            }}
          />
        </Tooltip>
        <Tooltip content={t("help.card.tourFrom", "Start the tour here")} relationship="description">
          <Button
            size="small"
            appearance="transparent"
            icon={<Play20Regular />}
            aria-label={t("help.card.tourFromAria", "Start the tour at: {{title}}", { title })}
            onClick={() => onStartTour(step)}
          />
        </Tooltip>
      </div>
    </article>
  );
});
